import { all, call, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as types from 'appRedux/actions/calendar/calendarModuleActionType';
import { API_URL_V1, API_PMS_HEADERS } from 'constants/ApiSetting';
import { APIPostRequest } from 'util/connection';
import { getItems } from 'util/localStorage';
import { userSignOut } from 'appRedux/actions/Auth';

interface RpcEnvelope {
  result_code?: string | number;
  result_msg?: string;
  result_data?: any;
  msg_code?: string | number;
}

interface RpcError extends Error {
  rpcNotFound?: boolean;
}

const rpcError = (message: string, notFound = false): RpcError =>
  Object.assign(new Error(message || 'Request failed'), { rpcNotFound: notFound });

/**
 * APIPostRequest resolves to the backend body `{ status, message, data: { result_code, result_msg, result_data } }`.
 * On a non-2xx HTTP status the connection util reshapes it to `{ status, data: { message, data: <envelope> } }`.
 * `result_code === "404"` / HTTP 404 means the RPC function is not deployed on this backend.
 */
function* postCalendarApi(path: string, params: Record<string, unknown>): Generator<any, RpcEnvelope, any> {
  const { access_token } = yield call(getItems, ['access_token']);
  API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;
  const response = yield call(
    APIPostRequest,
    `${API_URL_V1}pms/rpc/calendar/${path}`,
    params,
    API_PMS_HEADERS
  );

  if (response?.status === 200) {
    const envelope: RpcEnvelope = response.data ?? {};
    if (String(envelope.result_code) === '0') return envelope;
    throw rpcError(String(envelope.result_msg ?? ''), String(envelope.result_code) === '404');
  }

  if (response?.status === 403) yield put(userSignOut());
  const nested: RpcEnvelope = response?.data?.data ?? {};
  const notFound = response?.status === 404 || String(nested.result_code) === '404';
  throw rpcError(String(nested.result_msg ?? response?.data?.message ?? ''), notFound);
}

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : String(error));
const isNotFound = (error: unknown) => Boolean((error as RpcError)?.rpcNotFound);

function* listCalendars({ payload }: types.CalendarModuleListRequest): Generator<any, void, any> {
  try {
    const result = yield call(postCalendarApi, 'list', payload);
    yield put({
      type: types.CALENDAR_MODULE_LIST_SUCCESS,
      payload: Array.isArray(result.result_data) ? result.result_data : [],
    });
  } catch (error) {
    if (isNotFound(error)) {
      yield put({ type: types.CALENDAR_MODULE_UNAVAILABLE });
      return;
    }
    yield put({
      type: types.CALENDAR_MODULE_LIST_FAILURE,
      payload: { action: 'calendarList', status: false, message: errorMessage(error) },
    });
  }
}

function* listEvents({ payload }: types.CalendarEventListRequest): Generator<any, void, any> {
  try {
    const result = yield call(postCalendarApi, 'event/list', payload);
    const data = result.result_data ?? {};
    yield put({
      type: types.CALENDAR_EVENT_LIST_SUCCESS,
      payload: {
        items: Array.isArray(data.items) ? data.items : [],
        paging: data.paging ?? null,
      },
    });
  } catch (error) {
    if (isNotFound(error)) {
      yield put({ type: types.CALENDAR_MODULE_UNAVAILABLE });
      return;
    }
    yield put({
      type: types.CALENDAR_EVENT_LIST_FAILURE,
      payload: { action: 'eventList', status: false, message: errorMessage(error) },
    });
  }
}

function* listSources({ payload }: types.CalendarSourceListRequest): Generator<any, void, any> {
  try {
    const result = yield call(postCalendarApi, 'source/list', payload);
    yield put({
      type: types.CALENDAR_SOURCE_LIST_SUCCESS,
      payload: Array.isArray(result.result_data) ? result.result_data : [],
    });
  } catch (error) {
    if (isNotFound(error)) {
      yield put({ type: types.CALENDAR_MODULE_UNAVAILABLE });
      return;
    }
    yield put({
      type: types.CALENDAR_SOURCE_LIST_FAILURE,
      payload: { action: 'sourceList', status: false, message: errorMessage(error) },
    });
  }
}

function* lookupEvents({ payload }: types.CalendarLookupRequest): Generator<any, void, any> {
  try {
    const result = yield call(postCalendarApi, 'lookup', payload);
    yield put({
      type: types.CALENDAR_LOOKUP_SUCCESS,
      payload: Array.isArray(result.result_data) ? result.result_data : [],
    });
  } catch (error) {
    if (isNotFound(error)) {
      yield put({ type: types.CALENDAR_MODULE_UNAVAILABLE });
      return;
    }
    yield put({
      type: types.CALENDAR_LOOKUP_FAILURE,
      payload: { action: 'calendarLookup', status: false, message: errorMessage(error) },
    });
  }
}

function* mutateCalendar({ payload }: types.CalendarMutationRequestAction): Generator<any, void, any> {
  const { resource, operation, params } = payload;
  const actionName = `${resource}${operation.charAt(0).toUpperCase()}${operation.slice(1)}`;
  const path = operation === 'import' ? 'source/import' : `${resource === 'calendar' ? '' : `${resource}/`}${operation}`;

  try {
    const result = yield call(postCalendarApi, path, params);
    yield put({
      type: types.CALENDAR_MUTATION_SUCCESS,
      payload: {
        action: actionName,
        status: true,
        message: result.result_msg || 'Success',
        resultCode: String(result.result_code ?? '0'),
        resultData: result.result_data,
      },
    });
  } catch (error) {
    yield put({
      type: types.CALENDAR_MUTATION_FAILURE,
      payload: { action: actionName, status: false, message: errorMessage(error) },
    });
  }
}

function* watchCalendarModule() {
  yield takeLatest(types.CALENDAR_MODULE_LIST_REQUEST, listCalendars);
  yield takeLatest(types.CALENDAR_EVENT_LIST_REQUEST, listEvents);
  yield takeLatest(types.CALENDAR_SOURCE_LIST_REQUEST, listSources);
  yield takeLatest(types.CALENDAR_LOOKUP_REQUEST, lookupEvents);
  yield takeEvery(types.CALENDAR_MUTATION_REQUEST, mutateCalendar);
}

export default function* calendarModuleSaga() {
  yield all([fork(watchCalendarModule)]);
}
